import type { ReactNode } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';

import { useElevation } from '@/shared/elevation/useElevation';
import { useFeatures, useIsPlatform } from '@/shared/session/currentUser';
import { PageHeader } from '@/shared/ui/PageHeader';
import { cn } from '@/shared/utils/cn';

/** `platform: true` marks the provider's half (contract v10.11): Ignira alone sees it. */
const TABS = [
  { to: '/administracion/usuarios', label: 'Usuarios', platform: false },
  { to: '/administracion/buzones', label: 'Buzones', platform: false },
  { to: '/administracion/destinos', label: 'Destinos', platform: false },
  { to: '/administracion/activacion', label: 'Activación', platform: false },
  { to: '/administracion/aprendido', label: 'Lo que ha aprendido', platform: false },
  { to: '/administracion/accesos', label: 'Accesos', platform: true },
  { to: '/administracion/datos', label: 'Datos', platform: true },
  { to: '/administracion/correcciones', label: 'Correcciones', platform: true },
  { to: '/administracion/incidencias', label: 'Incidencias', platform: true },
  { to: '/administracion/fiabilidad', label: 'Fiabilidad', platform: true },
  { to: '/administracion/estadisticas', label: 'Estadísticas', platform: true },
  { to: '/administracion/documentos', label: 'Documentos', platform: true },
] as const;

const timeFormat = new Intl.DateTimeFormat('es-ES', { timeStyle: 'short' });

/** What every tab has in common: the password and the log. */
const COMMON_HELP: ReactNode[] = [
  <>
    Cada cambio aquí pide <strong>volver a escribir tu contraseña</strong> (vale cinco minutos) y
    queda anotado en el registro de accesos con quién y cuándo.
  </>,
];

/**
 * The help of each tab, behind the «?» of the header. What used to be a description
 * under each card title lives here, so the table or the form is the first thing on the
 * screen and the explanation is one click away when somebody needs it.
 */
const TAB_HELP: Record<string, ReactNode[]> = {
  usuarios: [
    <>
      Cada persona ve <strong>sus buzones</strong>, enteros, y ninguno más: se le asignan aquí
      (varios si hace falta, como compras@ y ventas@) y sin buzón la bandeja le sale vacía. Aparte,
      el interruptor <strong>Administra</strong> (Víctor y Ernesto) da esta pantalla (usuarios,
      buzones, destinos y activación), no más correo. Lo enciende y lo apaga un{' '}
      <strong>superadministrador</strong> (Ignira), que es el único que ve todos los buzones.
    </>,
    <>
      «Nueva invitación» y «Nueva contraseña» sirven para quien ha perdido el enlace o la
      contraseña; la segunda cierra sus sesiones abiertas.
    </>,
  ],
  buzones: [
    <>
      Si «Probar conexión» falla, no se guarda nada. El sistema relee esta lista cada minuto: dar de
      baja un buzón para su lectura, volver a leerlo la arranca, cambiar la contraseña la reinicia.
    </>,
  ],
  destinos: [
    <>
      Qué tipo de correo va a cada persona lo fija el sistema y no se cambia desde aquí: aquí solo
      se pone <strong>la dirección real</strong> de cada destino y el nombre de cada carpeta.
    </>,
    <>Cada tabla se guarda entera con su botón.</>,
  ],
  activacion: [
    <>
      El sistema aplica un cambio en menos de un minuto. Pasar el reenvío de un tipo de correo a
      «Automático» es la activación completa del contrato (cláusula 6.3): hace falta la conformidad
      escrita de Cubas, de un tipo en uno.
    </>,
    <>
      «Todo a solo proponer» deja cada buzón y tipo de correo en solo proponer en menos de un
      minuto; lo ya enviado no vuelve.
    </>,
  ],
  aprendido: [
    <>
      Cada criterio sale de una <strong>nota escrita al corregir</strong> un correo. Se aplica desde
      el siguiente correo y lo ya clasificado no cambia. «Solo ese correo» no cambia nada de los
      siguientes.
    </>,
    <>
      Desactivar lo saca del triaje; «Volver a activar» lo pone otra vez. Si un criterio se corrige
      más de lo que se confirma, no está acertando: edítalo o desactívalo.
    </>,
    <>Aquí no se pide la contraseña; cada cambio queda anotado en el registro de accesos.</>,
  ],
  accesos: [<>«Hasta» incluye el día entero que elijas.</>],
  datos: [
    <>
      La pantalla dice qué se borra, de dónde y por qué. Lo que se borra es{' '}
      <strong>la copia de la aplicación</strong>; el buzón de correo no se toca nunca.
    </>,
    <>
      «Qué se borraría hoy» es lo que borraría la pasada si corriera ahora mismo. Si crece día tras
      día, o la última pasada tiene más de una noche, la pasada automática no está corriendo.
    </>,
  ],
  correcciones: [
    <>
      Las fechas son las de <strong>llegada del correo</strong> (en ofertas, las de lectura): una
      descarga de «septiembre» lleva el correo de septiembre, se respondiera cuando se respondiera.
    </>,
    <>
      «Casos de eval» es para quien mejora el sistema: cada fila se lee antes de añadirla a las
      pruebas, porque los nombres de las firmas no siempre se detectan solos.
    </>,
  ],
  incidencias: [
    <>
      Cuando alguien te dé un <strong>código</strong> que ha visto en pantalla (LEC-01, TRI-02…),
      fíltralo aquí: verás cuántas veces ha pasado, a qué correos y desde qué despliegue.
    </>,
    <>
      Los códigos que terminan en 01, 02 o 03 son del proveedor (saturado, lento o caído) y la
      aplicación los reintenta sola; si un grupo crece día tras día, ahí hay algo que arreglar.
    </>,
  ],
  fiabilidad: [
    <>
      «Al menos» es lo que acierta como mínimo con las respuestas que hay (cota de Wilson al 80 %):
      3 de 3 se queda en un 65 %. Por eso hacen falta muchas seguidas antes de fiarse.
    </>,
    <>
      «Faltan N» quiere decir que la media ya supera el listón y solo falta volumen. «No llega», que
      falla más de lo que el listón permite: ahí hay algo que corregir antes.
    </>,
  ],
  documentos: [
    <>
      Se aplica a lo que entre desde ahora; lo ya archivado conserva su fecha. Lo que se borra pasa
      un tiempo en la papelera antes de irse del todo.
    </>,
    <>
      Facturas de proveedor, albaranes y presupuestos emitidos tienen un mínimo legal (Código de
      Comercio, art. 30) que ni este formulario ni el servidor dejan bajar.
    </>,
  ],
};

/**
 * The administration panel (contract v10): what lets the shop's management give access,
 * connect mailboxes and decide what the triage does, without a developer.
 *
 * Seven tabs, each a route, so a link to «Buzones» is a link. Everything that writes asks
 * the administrator to retype the password (`ElevationProvider`) and lands in the
 * access log; this shell only says so once, small, next to the subtitle.
 */
export function AdminPage() {
  const { elevatedUntil } = useElevation();
  const { pathname } = useLocation();
  const { document_archive: documentArchive } = useFeatures();
  const platform = useIsPlatform();
  const elevated = elevatedUntil !== null && elevatedUntil.getTime() > Date.now();
  const tab = pathname.split('/')[2] ?? '';
  // «Lo que ha aprendido» asks for no password (the API does not), so the common line
  // would be false there; its own help says what is true.
  const help =
    tab === 'aprendido' ? (TAB_HELP[tab] ?? []) : [...COMMON_HELP, ...(TAB_HELP[tab] ?? [])];
  // The platform half is Ignira's (contract v10.11); and, fase 1 (ADR 0016 del back), sin
  // el archivo documental la pestaña no lleva a ninguna parte (`RequireFeature` la manda
  // a la bandeja), así que no se enseña.
  const tabs = TABS.filter(
    (item) =>
      (platform || !item.platform) && (documentArchive || item.to !== '/administracion/documentos'),
  );

  return (
    <>
      <PageHeader
        title="Administración"
        description={
          <>
            <span>Quién entra y qué hace el sistema</span>
            {/* «Lo que ha aprendido» asks for no password: the line would be false there. */}
            {tab === 'aprendido' ? null : (
              <>
                <span className="text-ink-4 text-[12.5px]"> · </span>
                <span className="text-ink-4 text-[12.5px]">
                  {elevated && elevatedUntil
                    ? `modo elevado hasta las ${timeFormat.format(elevatedUntil)}`
                    : 'se pedirá la contraseña'}
                </span>
              </>
            )}
          </>
        }
        help={help}
      />

      {/* The row scrolls sideways on a phone, with no visible bar. `overflow-y-hidden`
          is not decorative: with `overflow-x-auto` alone the browser computes the
          vertical overflow as `auto` too, and the links' underline (2 px over the 1 px
          rule) was enough to grow a vertical scrollbar at 1440 px. */}
      <nav
        aria-label="Secciones de administración"
        className="border-line-2 -mx-1 flex gap-1 overflow-x-auto overflow-y-hidden border-b px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {tabs.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            className={({ isActive }) =>
              cn(
                'shrink-0 border-b-2 px-3 py-2 text-[14px] font-medium whitespace-nowrap',
                isActive
                  ? 'border-accent text-ink'
                  : 'text-ink-3 hover:text-ink border-transparent',
              )
            }
          >
            {tab.label}
          </NavLink>
        ))}
      </nav>

      <Outlet />
    </>
  );
}
